import React, { useContext, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import ApiService from '../../../../services/ApiService';
import { format } from 'date-fns';
import { Settings, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';

import { StockOutletType } from '..';
import { TypeMainOutlet } from '../..';
import { StockMovement } from './types';
import { ExitOrEntry, OrderCellSpan } from './style';
import { ConfigButton, GlobalTable, HeaderLine, Td, TdButtons, Th, Tr } from '../../../../components/styles/table-style';
import { TableContainer } from '../style';
import UserContext from '../../../../contexts/UserContext';
import TableFiltersContext from '../../../../contexts/TableFiltersContext';
import SectionContext from '../../../../contexts/SectionContext';
import StockContext from '../../../../contexts/StockContext';
import ModalContext from '../../../../contexts/ModalContext';
import { useTenant } from '../../../../contexts/TenantContext';
import undo from '../../../../assets/undo.svg';

const MovementTable: React.FC = () => {
  const { stockMode } = useContext(ModalContext);
  const { isFashionTenant } = useTenant();
  const isFashion = isFashionTenant || stockMode === 'fashion';
  const { user } = useContext(UserContext)
  const [permissions, setPermissions] = useState<string[]>([])
  const { details } = useContext(StockContext)
  const { refresh, setRefresh } = useContext(StockContext)
  const { movementFilters, globalFilter, setMovementFilters } = useContext(TableFiltersContext)
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([])
  const [count, setCount] = useState<number>(0)
  const { movementTypeFilter } = useContext(TableFiltersContext);
  const { setTabIndex, fields, setIsDropdownOpen, quantity, setQuantity } = useOutletContext<StockOutletType>()
  const { search } = useOutletContext<TypeMainOutlet>()
  const { setStockSection } = useContext(SectionContext)
  const [buttonLocker, setButtonLocker] = useState<boolean>(false)

  useEffect(() => {
    setTabIndex(1);
    setStockSection('movement');
    setQuantity(8);
  }, []);

  useEffect(() => {
    const parameters = {
      size: quantity,
      filters: movementFilters,
      global: search,
      movementType: movementTypeFilter
    };

    const fetchData = async () => {
      try {
        const response = await ApiService.movements.find(parameters)
        if (response?.data) {
          const items = Array.isArray(response.data) ? response.data : response.data.items || []
          setStockMovements(items)
        }
      } catch (error: any) {
        console.error(
          'Ocorreu um erro ao buscar os dados de movimento de estoque',
          error,
        )
        
        if (error.response?.status === 401) {
          localStorage.removeItem('token')
          window.location.href = '/'
        }
      }
    }

    const timeoutId = setTimeout(fetchData, 300);
    return () => clearTimeout(timeoutId);
  }, [quantity, movementFilters, movementTypeFilter, search, refresh]);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user.profileId) return;
      try {
        const response = await ApiService.profiles.findById(user.profileId);
        if (response.data) {
          setPermissions(response.data.permissions || []);
        }
      } catch (error) {
        console.log('Usuário sem perfil');
      }
    };
    fetchPermissions();
  }, [user.profileId]);

  const revertMovement = async (ploomesId: number) => {
    setButtonLocker(true)
    try {
      const response = await ApiService.reverseMovement.reverse(ploomesId, { userId: user.sub });
      if (response.data) {
        setCount(count + response.data);
      }
      setRefresh(prev => !prev)
      setButtonLocker(false)
      
    } catch (error: any) {
      if (error.response.status === 404) {
        alert(
          'Por favor, verifique na Ploomes se este negócio está nos conformes.',
        )
        setButtonLocker(false)
        setRefresh(prev => !prev)

      } else {
        console.error('Erro ao desfazer movimentação', error)
        setButtonLocker(false)
        setRefresh(prev => !prev)

      }
      setButtonLocker(false);
      setRefresh(prev => !prev);
    } finally {
      setCount((prevCount) => prevCount + 1);
    }
  };

  const formatNumber = (num: number) => num.toFixed(2);

  const getCellClass = (field: string) => {
    if (field.includes('Weight')) return 'weight-cell';
    if (field.includes('LengthInMm')) return 'length-cell';
    if (field === 'quantity') return 'quantity-cell';
    return '';
  };

  const handleIdClick = (id: number) => {
    if (id > 1) window.open(`https://app10.ploomes.com/deal/${id}`, '_blank');
  };

  const getSaleNumber = (orderName: string) => {
    const regex = /n[°º]\s?da venda:\s?(\d+)/i;
    const match = orderName.match(regex);
    if (match) {
      return match[1];
    }
    return orderName ? orderName.slice(-4) : '';
  };

  const renderOrderCell = (item: StockMovement) => {
    const { ploomesOrderId, orderName } = item;
    const maxLength = 25;
    const truncatedOrderName = orderName && orderName.length > maxLength 
      ? orderName.slice(0, maxLength) + '...' 
      : orderName;

    if (ploomesOrderId === 0) {
      return (
        <ExitOrEntry title='Entrada' className="flex items-center gap-2 entry">
          <ArrowDownCircle color="green" size={20} />
          <span className='inline-flex' title={orderName}>{truncatedOrderName}</span>
        </ExitOrEntry>
      );
    }

    if (ploomesOrderId === 1) {
      return (
        <ExitOrEntry title='Saída'>
          <ArrowUpCircle color='red' size={20} />
          <span title={orderName}>{truncatedOrderName}</span>
        </ExitOrEntry>
      );
    }

    return (
      <span
        title={orderName}
        onClick={() => handleIdClick(ploomesOrderId)}
        style={{ cursor: 'pointer', color: 'blue' }}
      >
        {orderName.substring(0, 25)}...
      </span>
    );
  };

  const filteredData = (item: StockMovement) => {
    if (movementTypeFilter === "Manual") {
      return item.ploomesOrderId === 0 || item.ploomesOrderId === 1;
    }
    if (movementTypeFilter === "Pedido") {
      return item.ploomesOrderId !== 0 && item.ploomesOrderId !== 1;
    }
    return true; // Se nenhum filtro estiver ativo, mostra tudo
  };
  
  return (
    <TableContainer>
      <GlobalTable>
        <thead>
        <HeaderLine>
          {fields.whichFieldsToShow.filter(field => field.checked).map((field, index) => (
            <Th key={index}>{field.fieldName}</Th>
          ))}
          <Th style={{ width: '40px', padding: 0 }}>
            <ConfigButton onClick={() => setIsDropdownOpen(prev => !prev)}><Settings size={20} /></ConfigButton>
          </Th>
        </HeaderLine>
      </thead>
      <tbody>
        {stockMovements.filter(filteredData).map((item) => (
          <Tr 
            key={item.id} 
            className={item.status === '410' ? 'yellowLine' : ''} 
            title={
              item.updatedBy?.name && item.updatedAt 
                ? `Atualizado por ${item.updatedBy.name} em ${
                  format(new Date(item.updatedAt), 'dd/MM/yyyy - HH:mm')}h${
                  item.observation ? `\nObservação: ${item.observation}` : ''
                }`
                : undefined
            }
          >
            {fields.whichFieldsToShow.filter(field => field.checked).map((field, index) => (
              <Td key={index} className={getCellClass(field.field)}>
                {field.field === 'createdAt' ? (
                  format(new Date(item.createdAt), 'dd/MM/yyyy - HH:mm') + 'h'
                ) : field.field === 'ploomesOrderId' ? (
                  renderOrderCell(item)
                ) : typeof item[field.field as keyof StockMovement] === 'number' ? (
                  isFashion &&
                  (field.field === 'oldTotalWeight' || field.field === 'newTotalWeight')
                    ? `${formatNumber(item[field.field as keyof StockMovement] as number)} kg`
                    : formatNumber(item[field.field as keyof StockMovement] as number)
                ) : field.field === 'status' ? (
                  item[field.field] === '410' ? <>Desfeito</> : <>Abatido</>
                ) : field.field === 'measurementUnit' ? (
                  item.unitOfMeasurement?.name || item.measurementUnit || 'N/A'
                ) : (
                  item[field.field as keyof StockMovement]
                )}
              </Td>
            ))}
          </Tr>
        ))}
      </tbody>
    </GlobalTable>
    </TableContainer>
  );
};

export default MovementTable;